import { clsx } from 'clsx';
import pluralize from 'pluralize';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import type { Actions, FormField } from '../../types/data-table';
import Toggle from '../toggle';

type Props = {
  actions: Actions;
  fields: FormField[];
  rowCount: number;
  tableName: string;
};

const DTCoreButtons = ({ actions, fields, rowCount, tableName }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clear, setClear] = useState<string | undefined>();
  const [selOptions, setSelOptions] = useState<{ name: string; value: string }[] | undefined>();
  const { bulkUpsert, confirmDeleteAll, deleteAll } = actions;
  const canDeleteAll = rowCount > 0;

  useEffect(() => {
    if (fields) {
      const newOptions = fields
        .filter((f) => f.bulk)
        .map((field) => {
          return { name: field.label, value: field.name };
        });
      setSelOptions(newOptions);
      setClear(newOptions[0]?.value);
    }
  }, [fields]);

  const handleClearColumn = () => {
    if (clear) {
      if (window.confirm(`Are you sure you want to clear the ${clear} for all?`)) {
        toast.promise(bulkUpsert(['all'], { [clear]: null }), {
          pending: 'Clearing all...',
          success: 'All cleared!',
          error: 'Error!',
        });
      }
    }
  };

  const handleDeleteAll = async () => {
    if (!canDeleteAll) return;

    const confirmed = confirmDeleteAll
      ? await confirmDeleteAll()
      : window.confirm('Are you sure you want to delete all?');
    if (!confirmed) return;

    toast.promise(deleteAll(), {
      pending: 'Deleting all...',
      success: 'All deleted!',
      error: 'Error!',
    });
  };

  return (
    <div className='w-full shadow-sm p-1'>
      <div className='w-full rounded-2xl bg-white'>
        <div className='text-gray-500'>
          <Toggle checked={isOpen} label={'Actions'} onChange={setIsOpen} />
          <div className={clsx(!isOpen && 'hidden', 'flex flex-col')}>
            <button
              type='button'
              disabled={!canDeleteAll}
              data-tip={canDeleteAll ? undefined : `No ${pluralize(tableName)} to delete`}
              className={clsx(
                'items-center mt-2 text-xs shadow-sm p-2 text-white rounded',
                canDeleteAll
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-gray-300 cursor-not-allowed hover:bg-gray-300',
              )}
              onClick={handleDeleteAll}
            >
              <span>Delete All</span>
            </button>

            <span
              className={clsx(
                !selOptions && 'hidden',
                selOptions && selOptions.length === 0 && 'hidden',
                'relative inline-flex items-center mt-2 p-2 text-xs hover:bg-blue-600 text-white rounded bg-blue-500',
              )}
            >
              <span
                className='relative inline-flex items-center rounded-l-md'
                data-tip='Clear a single column for all records'
              >
                <select
                  id={'reset-all'}
                  className={clsx(
                    'shadow-sm block w-full text-gray-900 text-xs border-gray-300 rounded-sm',
                  )}
                  onChange={(e) => setClear(e.target.value)}
                  name={'reset-all'}
                  placeholder={'Select'}
                  value={clear}
                >
                  {selOptions?.map(({ name, value }) => (
                    <option key={`${name}-${value}`} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </span>
              <button type='button' className='ml-2' onClick={handleClearColumn}>
                <span>Clear</span>
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DTCoreButtons;
